import * as React from 'react'
import { connect, Dispatch } from 'react-redux'
import { compose } from 'redux'
import {
  Field,
  reduxForm,
  InjectedFormProps,
  formValueSelector,
  change
} from 'redux-form'
import { asyncValidate, shouldAsyncValidate } from 'redux-form-yup'
import * as R from 'ramda'

import { Form as StyledForm } from '../../../Styled/Shared'
import ToggleView from '../ToggleView'
import UnitInput, { UnitConfig } from '../Inputs/UnitInput'
import { Slab } from '../formDataQueries'
import {
  getActiveSlab,
  EditableSlab,
  getDrTypeBeam,
  getOneSided
} from '../../../../reducers/project'
import { Store } from '../../../../reducers'
import { SlabSchema } from '../validationSchema'
import { updateSlab, toggleOtherSlabs } from '../../../../actions/project'
import { handleKeyDown, parseFloats } from './shared'
import CustomSelect, { SelectProps } from '../../../Shared/CustomSelect'
import Tooltip, { Side } from '../../../Shared/Tooltip'
const tooltips = require('../../../Shared/tooltips.json')

import {
  formatMm,
  unitConfigConversion,
  parseMm,
  formatKNm2,
  parseKNm2,
  convertSlabModels,
  filterSlabModels,
  isLocationNorthAmerica,
  convertTooltip
} from '../../../../location_changes/conversionHelper'

type ExternalProps = {
  slabTypes: Slab[]
  onSubmit: any // tslint:disable-line
}

type Props = ExternalProps &
  InjectedFormProps<EditableSlab> & {
    selectedType: string
    isDisabled: boolean
    isDrBeam: boolean
    slabId: string
    oneSided: boolean

    changeWeight: (weight: number) => void
    changeHeight: (height: number) => void
    changeModel: (model: string) => void
    toggleRest: (slabId: string, val: boolean) => void
  }

type State = {
  tooltip: {
    pos: { x: number; y: number }
    id: string
    text: string
  }
}

class SlabForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { tooltip: { id: '', pos: { x: 0, y: 0 }, text: '' } }

    this.showTooltip = this.showTooltip.bind(this)
    this.hideTooltip = this.hideTooltip.bind(this)
  }

  handleChange = value => {
    const slabInfo = R.find(R.propEq('model', value), this.props.slabTypes)
    if (!slabInfo) {
      return
    }

    const { weight, height } = slabInfo

    // This isn't super pretty but saves us the trouble of having a separate
    // action / reducer case for this.
    this.props.changeWeight(weight)
    this.props.changeHeight(height)
    this.props.changeModel(value)
  }

  parseOptions = (options, type: boolean) => {
    return options.map(option => {
      const value = type ? option.type : option.model
      return (
        <option
          value={value}
          key={option.id}
          disabled={type && value !== 'hollow-core'}
        >
          {convertSlabModels(value.charAt(0).toUpperCase() + value.slice(1))}
        </option>
      )
    })
  }

  showTooltip(event: React.MouseEvent<HTMLElement>) {
    const name = event.currentTarget.getAttribute('name')
    const id = event.currentTarget.getAttribute('id')
    const height = event.currentTarget.offsetHeight
    const rect = event.currentTarget.getBoundingClientRect()
    if (name) {
      const text = tooltips['slab' + name]
      this.setState({
        tooltip: {
          pos: { x: rect.left, y: rect.top + height / 2 },
          id: name ? name : '',
          text: text
        }
      })
    } else if (id) {
      const text = convertTooltip(tooltips['slab' + id])

      this.setState({
        tooltip: {
          pos: { x: rect.left, y: rect.top + height / 2 },
          id: id ? id : '',
          text: text
        }
      })
    }
  }

  hideTooltip() {
    this.setState({
      tooltip: { ...this.state.tooltip, id: '' }
    })
  }

  render() {
    const { slabTypes } = this.props
    const { tooltip } = this.state
    if (this.props.isDisabled) {
      return <div style={{ paddingBottom: '15px' }}>Slab is disabled.</div>
    }
    return (
      <StyledForm
        onKeyDown={handleKeyDown(this.props.handleSubmit)}
        onSubmit={this.props.handleSubmit}
      >
        <h4>Slab options</h4>
        {!isLocationNorthAmerica() && (
          <ToggleView title="Slab type">
            <label htmlFor="type">
              Slab type
              <Field
                className="dbs-slab-type-select"
                name="type"
                component={CustomSelect}
                props={
                  {
                    options: this.parseOptions(
                      R.uniqBy(st => st.type, slabTypes),
                      true
                    ),
                    handleChange: this.handleChange,
                    currentVal: this.props.initialValues.type
                  } as SelectProps
                }
                autoFocus={true}
              />
            </label>
            <label htmlFor="model">
              Slab model
              <Field
                name="model"
                component={CustomSelect}
                props={
                  {
                    options: this.parseOptions(
                      filterSlabModels(
                        this.props.slabTypes,
                        this.props.selectedType
                      ),
                      false
                    ),
                    handleChange: this.handleChange,
                    currentVal: this.props.initialValues.model
                  } as SelectProps
                }
              />
            </label>

            <Field
              name="topConcrete"
              component={UnitInput}
              // parse={intParser}
              // props={{ title: 'Top concrete', unit: 'mm' } as UnitConfig}
              parse={parseMm}
              format={formatMm}
              props={unitConfigConversion({
                title: 'Top concrete',
                unit: 'mm'
              } as UnitConfig)}
            />
          </ToggleView>
        )}

        <ToggleView title="Slab dimensions">
          {!isLocationNorthAmerica() && (
            <div
              id="height"
              onMouseEnter={this.showTooltip}
              onMouseLeave={this.hideTooltip}
            >
              <Field
                name="height"
                component={UnitInput}
                disabled={true}
                props={{ title: 'Slab depth', unit: 'mm' } as UnitConfig}
              />
            </div>
          )}

          {isLocationNorthAmerica() && (
            <label htmlFor="model">
              Slab depth
              <Field
                name="model"
                component={CustomSelect}
                props={
                  {
                    options: this.parseOptions(
                      filterSlabModels(
                        this.props.slabTypes,
                        this.props.selectedType
                      ),
                      false
                    ),
                    handleChange: this.handleChange,
                    currentVal: this.props.initialValues.model
                  } as SelectProps
                }
              />
            </label>
          )}

          <div
            id="weight"
            onMouseEnter={this.showTooltip}
            onMouseLeave={this.hideTooltip}
          >
            <Field
              name="weight"
              component={UnitInput}
              type="number"
              parse={parseKNm2}
              format={formatKNm2}
              props={unitConfigConversion({
                title: 'Slab weight',
                unit: 'kN/m2'
              } as UnitConfig)}
            />
          </div>
          <div
            id="span"
            onMouseEnter={this.showTooltip}
            onMouseLeave={this.hideTooltip}
          >
            <Field
              name="span"
              component={UnitInput}
              parse={parseMm}
              format={formatMm}
              props={unitConfigConversion({
                title: 'Slab span',
                unit: 'mm'
              } as UnitConfig)}
            />
          </div>
          <label>
            Slab only this side of the beam
            {/* The conditions here are a bit too complicated. Basically, the checkbox should
                always be ticked if the beam is DR type. If it isn't, we need to show a checkmark
                when one of the beams is disabled but it's not this one. */}
            <input
              type="checkbox"
              checked={
                this.props.isDrBeam ||
                (this.props.oneSided && !this.props.isDisabled)
              }
              disabled={this.props.isDrBeam}
              onChange={e =>
                this.props.toggleRest(
                  this.props.slabId,
                  e.currentTarget.checked
                )
              }
            />
          </label>
        </ToggleView>
        <Tooltip
          tooltip={{
            text: tooltip.text,
            pos: tooltip.pos,
            side: Side.Left,
            show: tooltip.id.length > 0
          }}
        />
      </StyledForm>
    )
  }
}

const selector = formValueSelector('slab')

const mapStateToProps = (state: Store.Complete) => ({
  initialValues: getActiveSlab(state),
  selectedType: selector(state, 'type'),
  isDisabled: selector(state, 'disabled'),
  slabId: selector(state, 'id'),
  isDrBeam: getDrTypeBeam(state),
  oneSided: getOneSided(state)
})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  changeWeight: weight => dispatch(change('slab', 'weight', weight)),
  changeHeight: height => dispatch(change('slab', 'height', height)),
  changeModel: model => dispatch(change('slab', 'model', model)),
  toggleRest: (slabId, val) => dispatch(toggleOtherSlabs(slabId, val))
})

export default compose(
  connect<{}, {}, ExternalProps>(
    mapStateToProps,
    mapDispatchToProps
  ),
  reduxForm<Readonly<EditableSlab>>({
    form: 'slab',
    asyncValidate: asyncValidate(SlabSchema),
    shouldAsyncValidate,
    onChange(
      values: EditableSlab,
      dispatch: Dispatch<Store.Complete>,
      // tslint:disable-next-line
      props: any
    ) {
      // We reset redux-form state when slab changes but that also triggers an onChange event
      // (as new values are initialized). We skip updateSlab() when values are equal to
      // avoid getting the "dirty bit" set.
      if (!R.equals(props.initialValues, values)) {
        dispatch(updateSlab(parseFloats(values, ['weight'])))
      }
    }
  })
)(SlabForm)



// WEBPACK FOOTER //
// ./src/Components/Workspace/Calculation/Editors/SlabEditor.tsx